import $ from 'jquery';
import EventHandler from '@pixelunion/events';
import debounce from 'just-debounce';
import Flickity from 'flickity';
import { classes, selectors } from '../Constants';

export default class ProductSlideshow {
  constructor(options = {}) {
    this.el = options.el || null;

    if (!this.el) {
      return console.warn('Unable to initiate ProductSlideshow.js');
    }

    this.mediaManager = options.mediaManager;
    this.flickity = null;

    this.staticGallery = false;

    this.onThumbnailClick = this._onThumbnailClick.bind(this);
    this.onThumbnailKeyPress = this._onThumbnailKeyPress.bind(this);

    this.$thumbnailsWrapper = $(selectors.product.slideshowThumbnailsWrapper, this.el);

    this.imageContainer = this.el.querySelector(selectors.product.slideshowImages);
    this.thumbnailContainer = this.el.querySelector(selectors.product.slideshowThumbnails);

    this.$images = $(selectors.product.slideshowImage, this.el);
    this.$thumbnails = $(selectors.product.slideshowThumbnail, this.el);

    this.thumbnailPrev = this.el.querySelector(selectors.product.slideshowThumbnailsPrev);
    this.thumbnailNext = this.el.querySelector(selectors.product.slideshowThumbnailsNext);

    this.$selectedThumbnail = $(selectors.product.slideshowThumbnailSelected, this.thumbnailContainer);
    this.selectedThumbnailIndex = this.$thumbnails.index(this.$selectedThumbnail);

    this.onResize = debounce(this._onResize, 15).bind(this);

    this._init();
  }

  switchImage(id) {
    const $image = $(`[data-product-slideshow-media="${id}"]`, this.el);
    const $thumbnail = $(`[data-product-slideshow-thumbnail="${id}"]`, this.el);

    this.$images.removeClass(classes.product.slideshowImageSelected);
    this.$thumbnails.removeClass(classes.product.slideshowThumbnailSelected);

    $image.addClass(classes.product.slideshowImageSelected);
    $thumbnail.addClass(classes.product.slideshowThumbnailSelected);

    this.$selectedThumbnail = $thumbnail;
    this.selectedThumbnailIndex = this.$thumbnails.index(this.$selectedThumbnail);
  }

  destroy() {
    window.removeEventListener('resize', this.onResize);
    this._destroyFlickity();
    this._destroyStaticGallery();
  }

  _init() {
    if (this._shouldInitFlickity()) {
      this._initFlickity();
    } else if (this._shouldInitStaticGallery()) {
      this._initStaticGallery();
    }

    window.addEventListener('resize', this.onResize);
  }

  _onResize() {
    if (this._shouldInitFlickity()) {
      if (this.staticGallery) {
        this._destroyStaticGallery();
      }

      this._initFlickity();
      this.flickity.select(this.selectedThumbnailIndex);
    } else if (this._shouldInitStaticGallery()) {
      if (this.flickity) {
        this._destroyFlickity();
      }

      this._initStaticGallery();
      this.$selectedThumbnail.trigger('click');
    }
  }

  _shouldInitStaticGallery() {
    return !!this.thumbnailContainer;
  }

  _shouldInitFlickity() {
    if (!this.thumbnailContainer) {
      return false;
    }

    const containerWidth = this.thumbnailContainer.getBoundingClientRect().width;

    let thumbnailsWidth = 0;

    this.$thumbnails.each((index, el) => {
      thumbnailsWidth += $(el).outerWidth(true);
    });

    return thumbnailsWidth > containerWidth;
  }

  _initFlickity() {
    if (this.flickity) {
      return;
    }
    this.$selectedThumbnail = $(selectors.product.slideshowThumbnailSelected, this.thumbnailContainer);
    this.selectedThumbnailIndex = this.$thumbnails.index(this.$selectedThumbnail);

    this.flickityEvents = new EventHandler();
    this.flickityEvents.register(this.thumbnailContainer, 'rimg:load', () => this.flickity && this.flickity.resize());

    this.flickity = new Flickity(this.thumbnailContainer, {
      accessibility: false,
      cellAlign: 'left',
      prevNextButtons: false,
      pageDots: false,
      initialIndex: this.selectedThumbnailIndex,
    });

    this.flickity.reloadCells();
    this.$thumbnailsWrapper.addClass(classes.product.slideshowThumbnailsFlickityEnabled);

    this.flickityEvents.register(this.thumbnailPrev, 'click', () => {
      const prev = this.$selectedThumbnail[0].previousElementSibling;
      if (!prev) return;
      this._onThumbnailSelect(prev);
      this.flickity.previous();
    });

    this.flickityEvents.register(this.thumbnailNext, 'click', () => {
      const next = this.$selectedThumbnail[0].nextElementSibling;
      if (!next) return;
      this._onThumbnailSelect(next);
      this.flickity.next();
    });

    this.flickityEvents.register(this.$thumbnailsWrapper[0], 'click', event => {
      if ('productSlideshowThumbnail' in event.target.dataset) {
        this.flickity.select(parseInt(event.target.dataset.productSlideshowThumbnailIndex, 10));
        this._onThumbnailSelect(event.target);
      }
    });

    this.flickityEvents.register(this.$thumbnailsWrapper[0], 'focusin', event => {
      // Selects thumbnails when tabbing, bringing focused thumbnail into view
      // Note that thumbnails can be selected in the flickity slider
      // without the corresponding media being selected in the main viewport.
      // Main viewport is only updated when thumbnail is selected with a
      // click event, which includes activation with the keyboard.
      if (!('productSlideshowThumbnail' in event.target.dataset)) return;
      this.flickity.select(parseInt(event.target.dataset.productSlideshowThumbnailIndex, 10));
    });
  }

  _onThumbnailSelect(thumbnail) {
    const $selectedElement = $(thumbnail);
    const imageId = $selectedElement.data('product-slideshow-thumbnail');
    const $image = $(`[data-product-slideshow-media="${imageId}"]`, this.el);
    const $thumbnail = $(`[data-product-slideshow-thumbnail="${imageId}"]`, this.el);

    this.$images.removeClass(classes.product.slideshowImageSelected);
    this.$thumbnails.removeClass(classes.product.slideshowThumbnailSelected);

    $image.addClass(classes.product.slideshowImageSelected);
    $thumbnail.addClass(classes.product.slideshowThumbnailSelected);

    this.mediaManager.onMediaSelect($image[0]);

    this.$selectedThumbnail = $thumbnail;
    this.selectedThumbnailIndex = this.$thumbnails.index(this.$selectedThumbnail);
  }

  _destroyFlickity() {
    if (this.flickity) {
      this.$thumbnailsWrapper.removeClass(classes.product.slideshowThumbnailsFlickityEnabled);
      this.flickityEvents.unregisterAll();
      this.flickity.destroy();
      this.flickity = null;
    }
  }

  _initStaticGallery() {
    if (this.staticGallery) {
      return;
    }

    this.$thumbnails.each((index, el) => el.addEventListener('click', this.onThumbnailClick));
    this.$thumbnails.each((index, el) => el.addEventListener('keypress', this.onThumbnailKeyPress));
    this.staticGallery = true;
  }

  _destroyStaticGallery() {
    if (this.staticGallery) {
      this.$thumbnails.each((index, el) => el.removeEventListener('click', this.onThumbnailClick));
      this.$thumbnails.each((index, el) => el.removeEventListener('keypress', this.onThumbnailKeyPress));
      this.staticGallery = false;
    }
  }

  _getSlideFigureByMediaId(id) {
    return this.el.querySelector(`[data-product-slideshow-media="${id}"]`);
  }

  _onThumbnailKeyPress(event) {
    if (event.key === ' ' || event.key === 'Enter') {
      // Prevent the default action to stop scrolling when space is pressed
      event.preventDefault();

      const imageId = $(event.currentTarget).data('product-slideshow-thumbnail');

      this.switchImage(imageId);
      this.mediaManager.onMediaSelect(this._getSlideFigureByMediaId(imageId));
    }
  }

  _onThumbnailClick(event) {
    const imageId = $(event.currentTarget).data('product-slideshow-thumbnail');

    this.switchImage(imageId);
    this.mediaManager.onMediaSelect(this._getSlideFigureByMediaId(imageId));
  }
}
